import { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Check, Copy, CreditCard, Landmark } from 'lucide-react'
import { fetchPayments, type PaymentListItem, type PaymentStatus } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { YearSelector } from '@/components/ui/year-selector'
import { cn } from '@/lib/utils'

const PAYMENT_STATUS_TABS: PaymentStatus[] = ['success', 'failed', 'cancelled', 'refunded']

export function PaymentsPage() {
  const { t, i18n } = useTranslation(['payments', 'common'])
  const { parent } = useAuthStore()
  const navigate = useNavigate()
  const [payments, setPayments] = useState<PaymentListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [activeTab, setActiveTab] = useState<PaymentStatus>('success')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleCopy = useCallback((ref: string) => {
    navigator.clipboard.writeText(ref)
    setCopiedId(ref)
    setTimeout(() => setCopiedId(null), 1500)
  }, [])

  useEffect(() => {
    const loadPayments = async () => {
      setLoading(true)
      try {
        const data = await fetchPayments()
        setPayments(data || [])
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'common' }))
      } finally {
        setLoading(false)
      }
    }

    if (parent?.id) {
      loadPayments()
    } else {
      setLoading(false)
    }
  }, [parent?.id, t])

  const getStatusVariant = (status: string): "success" | "warning" | "destructive" | "secondary" | "info" => {
    switch (status) {
      case 'success': return 'success'
      case 'pending': return 'warning'
      case 'processing': return 'info'
      case 'failed': return 'destructive'
      case 'cancelled': return 'secondary'
      case 'refunded': return 'warning'
      default: return 'secondary'
    }
  }

  const getPaymentTypeLabel = (type: string) => {
    return t(`types.${type}`, { ns: 'payments', defaultValue: type })
  }

  const getStatusLabel = (status: string) => {
    return t(`statuses.${status}`, { ns: 'payments', defaultValue: status })
  }

  const getPaymentMethodLabel = (method: string) => {
    return t(`paymentMethods.${method}`, { ns: 'payments', defaultValue: method })
  }

  // Available years from data
  const availableYears = useMemo(() => {
    return [...new Set(payments.map(p => new Date(p.created_at).getFullYear()))].sort((a, b) => b - a)
  }, [payments])

  // Payments filtered by year
  const yearFilteredPayments = useMemo(() => {
    return payments.filter(p => new Date(p.created_at).getFullYear() === selectedYear)
  }, [payments, selectedYear])

  // Counts per status (within selected year)
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const status of PAYMENT_STATUS_TABS) {
      counts[status] = yearFilteredPayments.filter(p => p.status === status).length
    }
    return counts
  }, [yearFilteredPayments])

  // Final filtered payments by year + status tab
  const filteredPayments = useMemo(() => {
    return yearFilteredPayments.filter(p => p.status === activeTab)
  }, [yearFilteredPayments, activeTab])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('navigation.payments', { ns: 'common' })}</h1>
        </div>
        <Button onClick={() => navigate('/payments/checkout')}>
          <CreditCard className="h-4 w-4 mr-1" />
          {t('navigation.makePayment', { ns: 'common' })}
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <YearSelector
          years={availableYears}
          selectedYear={selectedYear}
          onChange={setSelectedYear}
        />

        {/* Status Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg overflow-x-auto">
          {PAYMENT_STATUS_TABS.map((status) => (
            <button
              key={status}
              onClick={() => setActiveTab(status)}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
                activeTab === status
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {getStatusLabel(status)}
              {statusCounts[status] > 0 && (
                <span className={cn(
                  "ml-1.5 text-xs px-1.5 py-0.5 rounded-full",
                  status === 'success' && "bg-emerald-100 text-emerald-800",
                  status === 'failed' && "bg-destructive/10 text-destructive",
                  status === 'cancelled' && "bg-secondary text-secondary-foreground",
                  status === 'refunded' && "bg-amber-100 text-amber-800",
                )}>
                  {statusCounts[status]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive font-medium">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredPayments.length > 0 ? (
        <div className="space-y-3">
          {filteredPayments.map((payment) => (
            <Card key={payment.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                  <span className="text-muted-foreground">{t('paymentId', { ns: 'payments' })}:</span>
                  <div>
                    <Badge
                      variant="outline"
                      className="font-mono cursor-pointer hover:bg-muted transition-colors gap-1.5"
                      onClick={() => handleCopy(payment.merchant_reference)}
                    >
                      {payment.merchant_reference}
                      {copiedId === payment.merchant_reference ? (
                        <Check className="h-3 w-3 text-emerald-600" />
                      ) : (
                        <Copy className="h-3 w-3 text-muted-foreground" />
                      )}
                    </Badge>
                  </div>
                  <span className="text-muted-foreground">{t('amount', { ns: 'payments' })}:</span>
                  <span className="font-semibold tabular-nums">
                    {'\u20AC'}{parseFloat(payment.amount).toFixed(2)}
                  </span>
                  <span className="text-muted-foreground">{t('paymentStatus', { ns: 'payments' })}:</span>
                  <div>
                    <Badge variant={getStatusVariant(payment.status)}>
                      {getStatusLabel(payment.status)}
                    </Badge>
                  </div>
                  <span className="text-muted-foreground">{t('paymentType', { ns: 'payments' })}:</span>
                  <div className="flex flex-wrap gap-1">
                    {(payment.payment_types || []).map((type, idx) => (
                      <Badge key={idx} variant="secondary">
                        {getPaymentTypeLabel(type)}
                      </Badge>
                    ))}
                  </div>
                  <span className="text-muted-foreground">{t('paymentMethod', { ns: 'payments' })}:</span>
                  <span className="flex items-center gap-1">
                    {payment.payment_method_type === 'card' ? (
                      <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <Landmark className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    {getPaymentMethodLabel(payment.payment_method_type)}
                  </span>
                  <span className="text-muted-foreground">{t('date', { ns: 'payments' })}:</span>
                  <span>
                    {new Date(payment.created_at).toLocaleDateString(i18n.language === 'el' ? 'el-GR' : 'en-US', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </span>
                </div>

                {payment.bookings_info && payment.bookings_info.length > 0 && (
                  <div className="pt-0.5 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{t('relatedBookings', { ns: 'payments' })}:</p>
                    <div className="space-y-1">
                      {payment.bookings_info.map((booking) => (
                        <div
                          key={booking.id}
                          className="text-xs text-muted-foreground hover:text-foreground cursor-pointer flex items-center gap-1 transition-colors"
                          onClick={() => navigate(`/bookings/${booking.id}`)}
                        >
                          <span className="font-mono">{booking.code}</span>
                          <span>-</span>
                          <span>{booking.child_name}</span>
                          <span className="text-muted-foreground/60">({booking.event_name})</span>
                          {booking.payment_type && (
                            <Badge variant="outline" className="text-[10px] py-0 px-1">
                              {getPaymentTypeLabel(booking.payment_type)}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <CreditCard className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">{t('noPayments', { ns: 'payments', defaultValue: 'No payments yet' })}</p>
            <Button onClick={() => navigate('/payments/checkout')}>
              <CreditCard className="h-4 w-4 mr-1" />
              {t('navigation.makePayment', { ns: 'common' })}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
